import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de l'énergie libre de Gibbs - Calculez la spontanéité des réactions chimiques";
const DESCRIPTION =
  "Calculatrice de l'énergie libre de Gibbs - Calculez la spontanéité des réactions chimiques. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "gibbs free energy, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/gibbs-free-energy/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice de l'énergie libre de Gibbs",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "ScienceApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice Gibbs",
        item: OG_URL,
      },
    ],
  },
];

function GibbsFreeEnergyCalculator() {
  const [enthalpy, setEnthalpy] = useState("");
  const [entropy, setEntropy] = useState("");
  const [temperature, setTemperature] = useState("");
  const [tempUnit, setTempUnit] = useState("K");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateGibbs = () => {
    try {
      setError("");
      const H = parseFloat(enthalpy);
      const S = parseFloat(entropy);
      const T_raw = parseFloat(temperature);

      if (isNaN(H) || isNaN(S) || isNaN(T_raw)) {
        setResult(null);
        return;
      }

      let T_kelvin = T_raw;
      if (tempUnit === "C") T_kelvin = T_raw + 273.15;
      if (tempUnit === "F") T_kelvin = (T_raw - 32) * (5 / 9) + 273.15;

      if (T_kelvin < 0) {
        setError("La température ne peut pas être inférieure au zéro absolu.");
        setResult(null);
        return;
      }

      const S_kJ = S / 1000;
      const deltaG = H - T_kelvin * S_kJ;

      let spontaneity = "";
      let color = "";

      if (deltaG < 0) {
        spontaneity = "Réaction spontanée";
        color = "#1a5f3f";
      } else if (deltaG > 0) {
        spontaneity = "Réaction non spontanée";
        color = "#b02a37";
      } else {
        spontaneity = "La réaction est à l'équilibre";
        color = "#084298";
      }

      setResult({
        deltaG: deltaG.toFixed(4),
        spontaneity,
        color,
        T_kelvin: T_kelvin.toFixed(2),
      });
    } catch (err) {
      setError("Une erreur s'est produite lors du calcul.");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateGibbs();
  }, [enthalpy, entropy, temperature, tempUnit]);

  const handleClear = () => {
    setEnthalpy("");
    setEntropy("");
    setTemperature("");
    setTempUnit("K");
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice de l'énergie libre de Gibbs</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                La <strong>calculatrice de l'énergie libre de Gibbs</strong> est utilisée pour déterminer si une réaction chimique se produira spontanément ou non. En entrant le changement d'enthalpie (Enthalpy), le changement d'entropie (Entropy) et la température, vous pouvez déterminer la valeur <strong>ΔG</strong> et l'état de spontanéité de la réaction immédiatement.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Entrez les données de la réaction chimique</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label>Changement d'enthalpie ΔH (kJ/mol)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={enthalpy}
                    onChange={(e) => setEnthalpy(e.target.value)}
                    placeholder="Par exemple : -92.2"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Changement d'entropie ΔS (J/mol·K)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={entropy}
                    onChange={(e) => setEntropy(e.target.value)}
                    placeholder="Par exemple : 198.8"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label>Température</label>
                  <input
                    type="number"
                    className="form-control"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    placeholder="Par exemple : 298"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Unité de température</label>
                  <select
                    className="form-control"
                    value={tempUnit}
                    onChange={(e) => setTempUnit(e.target.value)}
                  >
                    <option value="K">Kelvin (K)</option>
                    <option value="C">Celsius (°C)</option>
                    <option value="F">Fahrenheit (°F)</option>
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <button className="btn btn-success" onClick={handleClear}>
                    Effacer
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {result && (
              <div className="p-3 mb-3 rounded" style={{ backgroundColor: result.color }}>
                <div className="text-white">
                  <h3>Résultats :</h3>
                  <p><strong>ΔG = {result.deltaG} kJ/mol</strong></p>
                  <p><strong>Température = {result.T_kelvin} K</strong></p>
                  <p><strong>État : {result.spontaneity}</strong></p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      <br />
    </div>
  );
}
export default GibbsFreeEnergyCalculator;
